import * as React from 'react';
import { Div, Image } from '@kizi/core-components';
import { TabDefinition } from '@kizi/shared-components';
import { PriceProps, sendCustomEvent } from '..';
import { getLabel } from '../../utils/translations';

import {
  ProductOverviewModalProps,
  ProductTabView,
  TopPanel,
} from '../ProductOverviewModal';

import { ProductPackageDetailsTripplePlay } from './ProductPackageDetailsTripplePlay';
import { Window } from '../Window';

import { Services } from '@kizi/comparator-core';
import { Testoordel } from './Testoordel';
import { ProductOverviewModalHeader } from '../ProductOverviewModalHeader';

type ContentResponse = {
  providerDescription: string;
  providerId: string;
  providerImageSource: string;
  providerName: string;
  priceProps: PriceProps;
  tabs: TabDefinition[];
};

export const getContent = (
  product,
  priceMode: 'month' | 'year',
  services: Services
): ContentResponse => {
  const { provider, price } = product;
  const {
    description: providerDescription,
    id: providerId,
    name: providerName,
  } = provider;

  const priceProps = {
    discount: price[priceMode].discountIncluded,
    label: `per ${getLabel(priceMode)}`,
    value: product?.priceInfo?.total,
  };
  const providerImageSource = product.image;

  return {
    providerDescription,
    providerId,
    providerImageSource,
    providerName,
    priceProps,
    tabs: [
      {
        label: 'Pakket',
        content: (
          <ProductPackageDetailsTripplePlay
            content={services}
            description={providerDescription}
          />
        ),
      },
      ...(product.scoreEntity
        ? [
            {
              label: 'Testoordeel',
              content: <Testoordel scoreInput={product.scoreEntity} />,
            },
          ]
        : []),
    ],
  };
};

export const ProductOverviewModalTriplePlay: React.FC<
  ProductOverviewModalProps & { services: Services }
> = ({
  startIndex,
  product,
  priceMode,
  onClose,
  services,
  onSubmit,
  ...restProps
}) => {
  const purchasableProduct =
    (product as any)?.availability?.acceptance?.accepted !== false;

  const contentProps = getContent(product, priceMode, services);

  const { providerImageSource, providerName, priceProps } = contentProps;

  const show = startIndex > -1;

  if (!show) return null;

  sendCustomEvent('productoverview-modal', {});

  const content = (
    <ModalContent
      {...contentProps}
      onClose={onClose}
      onSubmit={onSubmit}
      purchasableProduct={purchasableProduct}
      startIndex={startIndex}
    />
  );

  return (
    <Window
      bottomContent={
        <ProductOverviewModalHeader
          priceProps={priceProps}
          purchasableProduct={purchasableProduct}
          onApplyClick={onSubmit}
          isOverstappen={false}
        />
      }
      modalProps={{
        noCloseIcon: true,
        noHorizontalMargin: true,
        children: content,
      }}
      noHorizontalPadding
      type="modal"
      title={
        <Image
          alt={providerName}
          height={28}
          src={providerImageSource}
          width="auto"
        />
      }
      onClose={onClose}
      show={true}
      {...restProps}
    >
      {content}
    </Window>
  );
};

interface ModalContentProps {
  purchasableProduct: boolean;
  providerName: string;
  providerImageSource: string;
  priceProps: PriceProps;
  onClose: React.MouseEventHandler<Element>;
  onSubmit: React.MouseEventHandler<HTMLButtonElement>;
  tabs: TabDefinition[];
  startIndex: number;
}

export const ModalContent: React.FC<ModalContentProps> = ({
  purchasableProduct,
  priceProps,
  providerImageSource,
  onClose,
  onSubmit,
  providerName,
  tabs,
  startIndex,
}) => {
  return (
    <>
      <Div variant={purchasableProduct ? '' : 'grayedContainer'}>
        <Div display={['none', null, 'flex']} variant="productModalTopPanel">
          <TopPanel
            providerName={providerName}
            providerImageSource={providerImageSource}
            priceProps={priceProps}
            onClose={onClose}
            onSubmit={onSubmit}
            purchasableProduct={purchasableProduct}
            isOverstappen={false}
          />
        </Div>
      </Div>
      <Div bg="white" borderBottomRightRadius={2} borderBottomLeftRadius={2}>
        <ProductTabView tabs={tabs} startIndex={startIndex} />
      </Div>
    </>
  );
};
